/**
 * Reconciliation Panel Component
 * Shows suggested matches between bank transactions and invoices.
 * Allows user to confirm each match to create a payment in Xero.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchReconciliationSuggestions,
  applyReconciliation,
} from '@/api/metricsApi';
import type { ReconciliationMatch } from '@/api/metricsApi';
import { formatCurrency } from '@/utils/formatters';
import './ReconciliationPanel.css';

interface ReconciliationPanelProps {
  onReconciled?: () => void;
}

export function ReconciliationPanel({ onReconciled }: ReconciliationPanelProps) {
  const [matches, setMatches] = useState<ReconciliationMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchReconciliationSuggestions();
      setMatches(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleReconcile = async (match: ReconciliationMatch) => {
    setApplying(match.bankTransactionID);
    setApplyError(null);
    try {
      await applyReconciliation({
        invoiceID: match.invoiceID,
        bankTransactionID: match.bankTransactionID,
        amount: match.bankAmount,
        date: new Date().toISOString().split('T')[0],
      });

      // Remove reconciled match from the list
      setMatches((prev) =>
        prev.filter((m) => m.bankTransactionID !== match.bankTransactionID)
      );

      onReconciled?.();
    } catch (err) {
      setApplyError(
        err instanceof Error ? err.message : 'Failed to apply reconciliation'
      );
    } finally {
      setApplying(null);
    }
  };

  const confidenceBadgeClass = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'recon-panel__confidence--high';
      case 'medium':
        return 'recon-panel__confidence--medium';
      case 'low':
        return 'recon-panel__confidence--low';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="recon-panel recon-panel--loading">
        <div className="loading-spinner"></div>
        <p>Finding matches...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recon-panel recon-panel--error">
        <p>{error}</p>
        <button className="recon-panel__retry" onClick={loadSuggestions}>
          Retry
        </button>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="recon-panel recon-panel--empty">
        <p>No suggested matches found.</p>
      </div>
    );
  }

  return (
    <div className="recon-panel">
      <h3 className="recon-panel__title">Suggested Matches</h3>
      {applyError && (
        <div className="recon-panel__apply-error" role="alert">
          {applyError}
        </div>
      )}
      <div className="recon-panel__list">
        {matches.map((match) => (
          <div
            key={match.bankTransactionID}
            className={`recon-panel__match${applying === match.bankTransactionID ? ' recon-panel__match--applying' : ''}`}
          >
            <div className="recon-panel__match-info">
              <div className="recon-panel__match-row">
                <span className="recon-panel__match-label">Bank:</span>
                <span className="recon-panel__match-value">
                  {formatCurrency(match.bankAmount)}
                  {match.bankReference && (
                    <span className="recon-panel__ref"> ref: {match.bankReference}</span>
                  )}
                </span>
              </div>
              <div className="recon-panel__match-arrow" aria-hidden="true">→</div>
              <div className="recon-panel__match-row">
                <span className="recon-panel__match-label">Invoice:</span>
                <span className="recon-panel__match-value">
                  {match.invoiceNumber} ({match.contactName}) — {formatCurrency(match.invoiceAmount)}
                </span>
              </div>
            </div>
            <div className="recon-panel__match-actions">
              <span
                className={`recon-panel__confidence ${confidenceBadgeClass(match.confidence)}`}
                title={match.reason}
              >
                {match.confidence}
              </span>
              <button
                className="recon-panel__reconcile-btn"
                onClick={() => handleReconcile(match)}
                disabled={applying !== null}
                aria-label={`Reconcile ${match.invoiceNumber}`}
              >
                {applying === match.bankTransactionID ? 'Reconciling...' : 'Reconcile'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
